import React from "react";
import moment from "moment";
import NflUtil from "../util/nflUtil";

export default class AllNflGamesHelper {
  constructor(allNflGamesData) {
    this.data = allNflGamesData;
  }

  getMatchUpTextNew(nflGame, nflTeamAbbr) {
    if (!nflGame) return null;

    if (!nflGame.hasStarted) {
      // pregame

      let startDate;
      if (nflGame.startDate) startDate = moment(nflGame.startDate);

      let text;
      let color = "#000000";
      let fontWeight = "normal";

      const isSoon = moment().add(1, "hours").isAfter(startDate);
      const isToday = moment().add(16, "hours").isAfter(startDate);
      const isWithinOneWeek = moment().add(7, "days").isAfter(startDate);

      const formatDate = isWithinOneWeek ? "ddd" : "ddd M/D";
      const formatTime = "h:mma";

      let spanTitle = null;
      if (startDate) {
        text =
          startDate.format(formatDate) + " " + startDate.format(formatTime);

        if (isToday) {
          color = "#0078f8";
          fontWeight = "bold";
          text = startDate.format(formatTime);
        }

        if (isSoon) {
          text = startDate.fromNow();
        }

        spanTitle = startDate.format("llll");
      }

      return (
        <span title={spanTitle} style={{ color, fontWeight }}>
          {text}
        </span>
      );
    } else if (!nflGame.hasEnded) {
      // game is live
      const score = this.getScoreForTeam(nflGame, nflTeamAbbr);
      return (
        <span>
          <span style={{ color: "#00ab00", fontWeight: "bold" }}>LIVE</span>{" "}
          {score}
        </span>
      );
    } else {
      // game is over

      return this.getResultForTeam(nflGame, nflTeamAbbr);
    }
  }

  /**
   *
   * @param week
   * @param nflTeamAbbr
   * @returns {null|*}
   */
  getTeamGame(week, nflTeamAbbr) {
    if (!(nflTeamAbbr && this.data?.nflGames?.weeks.hasOwnProperty(week)))
      return null;

    const games = this.data.nflGames.weeks[week];
    const teamGames = Object.keys(games)
      .map((id) => games[id])
      .filter((game) =>
        [game.teams?.away?.abbr, game.teams?.home?.abbr].includes(nflTeamAbbr)
      );
    if (teamGames.length === 1) return teamGames[0];

    return null;
  }

  /**
   *
   * @param week
   * @param nflTeamAbbr
   * @returns {?string}
   */
  getMatchupText(week, nflTeamAbbr) {
    const nflGame = this.getTeamGame(week, nflTeamAbbr);
    if (!nflGame) return null;

    const { away, home } = nflGame.teams;

    const team = away.abbr === nflTeamAbbr ? away : home;
    const at = !team.isHome ? "at " : "";

    const opp = away.abbr === nflTeamAbbr ? home : away;
    const oppAbbr = NflUtil.formatNflTeamAbbr(opp.abbr);

    return at + oppAbbr;
  }

  /**
   *
   * @param nflGame
   * @returns {?string}
   */
  getMatchupTime(nflGame) {
    if (!nflGame) return null;

    let startDate;
    if (nflGame.startDate) startDate = moment(nflGame.startDate);

    // if (!startDate) console.error("could not find startDate for nfl game");

    let hasStarted = !!nflGame.hasStarted;
    let hasEnded = !!nflGame.hasEnded;

    if (!hasStarted && startDate) {
      // double check with startDate
      const now = moment();
      hasStarted = startDate.isBefore(now);
    }

    let text;
    let color = "#000000";
    let fontWeight = "normal";

    if (hasStarted && !hasEnded) {
      color = "#00ab00";
      text = "LIVE";
    } else {
      if (!startDate) return null;

      const isSoon = moment().add(1, "hours").isAfter(startDate);
      const isToday = moment().add(16, "hours").isAfter(startDate);
      const isWithinOneWeek = moment().add(7, "days").isAfter(startDate);

      const formatDate = isWithinOneWeek ? "ddd" : "ddd M/D";
      const formatTime = "h:mma";

      text = startDate.format(formatDate) + " " + startDate.format(formatTime);

      if (!hasStarted) {
        if (isToday) {
          color = "#00ab00";
          fontWeight = "bold";
          text = startDate.format(formatTime);
        }

        if (isSoon) {
          text = startDate.fromNow();
        }
      }
    }

    return (
      <span title={startDate.format("llll")} style={{ color, fontWeight }}>
        {text}
      </span>
    );
  }

  getResultForTeam(nflGame, nflTeamAbbr) {
    if (!nflGame || !nflGame.hasEnded) return null;

    const { team } = this.getTeamAndOpp(nflGame, nflTeamAbbr);

    let color = "#000";

    if (team.result === "W") {
      color = "#00ab00";
    } else if (team.result === "L") {
      color = "#f00";
    }

    const score = this.getScoreForTeam(nflGame, nflTeamAbbr);

    return (
      <span>
        <span style={{ color }}>{team.result}</span> {score}
      </span>
    );
  }

  getScoreForTeam(nflGame, nflTeamAbbr) {
    if (!nflGame || !nflGame.hasStarted) return null;

    const { team, opp } = this.getTeamAndOpp(nflGame, nflTeamAbbr);

    return `${team.score}-${opp.score}`;
  }

  getTeamAndOpp(nflGame, nflTeamAbbr) {
    let team = null,
      opp = null;

    if (nflGame) {
      const { teams } = nflGame;
      const { away, home } = teams;

      if (away.abbr === nflTeamAbbr) {
        team = away;
        opp = home;
      } else if (home.abbr === nflTeamAbbr) {
        team = home;
        opp = away;
      } else {
        console.error(`cannot find team for game with abbr: ${nflTeamAbbr}`);
      }
    }

    return { team, opp };
  }

  getWeekGames(week) {
    return Object.keys(this.data.nflGames.weeks[week]).map(
      (gameId) => this.data.nflGames.weeks[week][gameId]
    );
  }

  haveAllNflGamesStarted(week) {
    const games = this.getWeekGames(week);
    return games.every((game) => this.hasNflGameStarted(game));
  }

  haveAnyNflGamesStarted(week) {
    const games = this.getWeekGames(week);
    return games.some((game) => this.hasNflGameStarted(game));
  }

  haveAllNflGamesEnded(week) {
    const games = this.getWeekGames(week);
    return games.every((game) => game.hasEnded);
  }

  hasNflGameStarted(nflGame) {
    if (!nflGame) return false;
    return nflGame.hasStarted || moment(nflGame.startDate).isBefore(moment());
  }
}
